import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { adjustTiersValues, isAddingContent, isSameDimension, mirrorContent, syncTiers } from "@/PayPlans/helpers/Tiers";
import { isLastValid, isValidMatrixRange } from "@/Common/helpers/Matrices/tiers";

// Hooks
import { detachValidate, isValidMapErrors } from "@/Common/hooks/Validations";

// Layouts
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import DefaultTiersLoader from "@/Layouts/components/Skeletons/DefaultTiersLoader/DefaultTiersLoader";
import TabLoader from "@/Layouts/components/Skeletons/TabLoader";
import TabSelectable from "@/Layouts/components/Tab/TabSelectable";

// Custom components
import MarketShare from "../MarketShare";

// MUI
import { Grid } from "@mui/material";

const getMakes = (makeMetrics = [], excludesMakes = []) => {
	const makes = makeMetrics.filter((element) => element.make && !excludesMakes.includes(element.make)).map((item) => item.make);
	return [...new Set(makes)];
};

const getContentTabs = (makes = []) => [...makes];

const mapModelToPropagate = (content, tabs = null) => {
	return {
		...content,
		simultaneousMarketShare: Boolean(tabs?.simultaneousMarketShare),
	};
};

const validateBeforeSend = (tiers = []) => {
	const validation = detachValidate([
		{
			definition: {
				useKey: "nationalRanks",
				operators: {
					conditions: [
						{
							type: "INLINE",
							on: "value",
							compare: "<", // If from < to is good,
						},
						{
							type: "STEP",
							on: "value",
							compare: "===",
							deltas: { Percent: 0.01 }, // Add delta on upper value
							distinct: 0,
						},
					],
				},
				errorsMap: ["from", "to"],
				message: "Invalid tiers",
				validationType: "DOUBLE_INPUT",
				specialProperties: {
					noFirstError: true,
					noLastError: true,
				},
			},
			section: tiers || [],
		},
		{
			definition: {
				useKey: "regionalRanks",
				operators: {
					conditions: [
						{
							type: "INLINE",
							on: "value",
							compare: "<", // If from < to is good,
						},
						{
							type: "STEP",
							on: "value",
							compare: "===",
							deltas: { Percent: 0.01 }, // Add delta on upper value
						},
					],
				},
				errorsMap: ["from", "to"],
				message: "Invalid Market Share Growth tiers",
				validationType: "DOUBLE_INPUT",
				specialProperties: {
					noFirstError: true,
					noLastError: true,
				},
			},
			section: tiers?.[0]?.bonuses || [],
		},
		{
			definition: {
				useKey: "bonuses",
				operators: {
					conditions: [
						{
							order: "ROWS",
							type: "STEP",
							on: "value",
							compare: "<=", // If current <= next is good,
						},
						{
							order: "COLUMNS",
							type: "STEP",
							on: "value",
							compare: "<",
							deltas: { Dollar: 0, Percent: 0 },
						},
					],
				},
				errorsMap: ["bonus"],
				errorsMapOn: "bonuses",
				message: "Incorrect bonuses definition",
				validationType: "MATRIX",
			},
			section: tiers,
		},
	]);
	return isValidMapErrors(validation);
};

const isAllValid = (errors = []) => !errors.length || [...errors].every((error) => error);

const isTierConditionsValid = (tiers = []) => {
	const isLastBonusValid = isLastValid(tiers, { column: "bonuses", reference: "bonus" });
	const isMatrixInRange = isValidMatrixRange(tiers, { column: "bonuses" });
	return isLastBonusValid && isMatrixInRange;
};

const isSomeConfiguredModel = (content) => {
	const { makes = [] } = content || {};
	if (!makes?.length) {
		return true;
	}
	return makes?.some((item) => isTierConditionsValid(item?.tiers || []));
};

export default function MarketShareMakes(props) {
	const {
		sectionId = "marketShare",
		content = {
			makes: [],
			simultaneousMarketShare: false,
		},
		excludesMakes = [],
		loading = false,
		readOnly = false,
		useSectionsErrors = () => {},
		onActionUpdate = () => {},
	} = props;

	// Hooks
	const { errors, getErrorKey, addSectionError, removeSectionError, clearSectionErrors } = useSectionsErrors?.() || {};

	// Franchises/Makes treatments
	const makes = useMemo(() => getMakes(content?.makes || [], excludesMakes), [content, excludesMakes]);

	// References
	const tabInformation = useRef({ index: 0, label: "" });

	// States
	const [model, setModel] = useState(null);
	const isSomeModelConfigured = isSomeConfiguredModel(model?.content);

	const { makes: innerMakes = [] } = model?.content || {};

	// Error handler
	const tabsErrors = useMemo(() => {
		if (errors.size && makes?.length) {
			const localErrors = Array.from(Array(makes.length)).map(() => false);
			const listErrors = errors?.get(sectionId) || []; // Ids that have errors

			makes.forEach((make, index) => {
				const errorKey = getErrorKey(sectionId, make);
				localErrors[index] = Boolean(listErrors.includes(errorKey));
			});

			return localErrors;
		}
		return [];
	}, [sectionId, makes, errors, getErrorKey]);

	// Actions handlers
	const updateAll = (section, data) => {
		setModel({ ...data });
		const storedErrors = Object.values(data?.content?.makes)?.map((make) => validateBeforeSend(make?.tiers || []));
		const tiersInRange = Object.values(data?.content?.makes)?.map((make) => isTierConditionsValid(make?.tiers || []));
		const dataToPropagate = mapModelToPropagate(data.content, data.tabsProps);
		const isValidModel = isAllValid(storedErrors);
		const hasTiersInRange = tiersInRange.some((item) => item);
		onActionUpdate?.(section, null, { ...dataToPropagate }, isValidModel && hasTiersInRange);
	};

	const synchronizeMakes = useCallback((args) => {
		const { updatedMakes = [], isSimultaneousMarketShare = false, isAdding = false, mirrorFromIndex = 0 } = args || {};
		if (isSimultaneousMarketShare) {
			// Synchronize tiers
			let result = syncTiers([...updatedMakes], isAdding, "tiers", "bonuses");

			result = adjustTiersValues(
				[...result],
				isAdding,
				{
					key: "tiers",
					values: [{ "from.value": { min: 0, max: 0, notReduce: true } }, { "to.value": { min: 0, max: 100 } }],
				},
				{
					key: "bonuses",
					values: [{ "from.value": { min: -100, max: -100, notReduce: true } }, { "to.value": { min: 0, max: 100 } }],
				}
			);

			const indexFrom = mirrorFromIndex < result.length ? mirrorFromIndex : 0;
			result = mirrorContent(result, indexFrom, { key: "tiers", values: [] }, { key: "bonuses", values: ["bonus"] });
			return result;
		}
		return updatedMakes;
	}, []);

	const handleUpdateSwitches = (section, area, value) => {
		const updatedModel = { ...model };

		// Updating current switch
		updatedModel["tabsProps"][area] = value;

		const isSimultaneousMarketShare = updatedModel["tabsProps"]["simultaneousMarketShare"];

		updatedModel["tabsProps"]["tabs"] = getContentTabs(makes);

		// Use to know if the reduction should be applied
		const needsToSynchronize = isSameDimension(model?.content, "tiers", "bonuses");
		updatedModel["content"]["makes"] = synchronizeMakes({
			isSimultaneousMarketShare,
			isAdding: needsToSynchronize,
			updatedMakes: updatedModel["content"]["makes"],
			mirrorFromIndex: tabInformation?.current?.index || 0,
		});

		updateAll(section, updatedModel);
	};

	const handleUpdate = (section, area, data) => {
		const updatedModel = { ...model };
		removeSectionError(section, area); // Remove section error

		const previousData = { ...model?.content?.makes?.find((item) => item.make === area) };
		updatedModel?.content.makes?.forEach((element, key) => {
			if (element.make === area) {
				updatedModel["content"]["makes"][key] = { ...element, ...data };
			}
		});

		const isSimultaneousMarketShare = updatedModel["tabsProps"]["simultaneousMarketShare"];
		const { isAddingRows, isAddingColumns } = isAddingContent(data, previousData, "tiers", "bonuses");
		const isAdding = isAddingRows && isAddingColumns;
		updatedModel["content"]["makes"] = synchronizeMakes({
			isSimultaneousMarketShare,
			isAdding,
			updatedMakes: updatedModel["content"]["makes"],
			mirrorFromIndex: tabInformation?.current?.index || 0,
		});

		updateAll(section, updatedModel);
	};

	const handleValidation = (section, area, value) => {
		value ? removeSectionError(section, area) : addSectionError(section, area);
	};

	useEffect(() => {
		setModel({
			tabsProps: {
				tabs: getContentTabs([...makes]),
				simultaneousMarketShare: Boolean(content.simultaneousMarketShare),
				errors: [],
			},
			content: clone(content),
		});

		return () => {
			setModel(null);
			tabInformation.current = { index: 0, label: "" };
		};
	}, [content, makes]);

	useEffect(() => {
		return () => {
			clearSectionErrors(sectionId);
		};
	}, [sectionId, clearSectionErrors]);

	return (
		<Grid container>
			<Grid item xs={12} mt={2.5}>
				{loading ? (
					<Grid>
						<TabLoader switch bottomSwitch />
						<DefaultTiersLoader mt={1} buttons />
					</Grid>
				) : (
					Boolean(model?.content?.makes?.length) && (
						<TabSelectable
							tabs={model?.tabsProps.tabs || []}
							errors={tabsErrors}
							content={innerMakes.map((item, key) => (
								<Grid container key={`${item.make}_${key}`}>
									<Grid item xs={12} className={"models-tiered-area"}>
										<MarketShare
											{...item}
											isSomeConfigured={isSomeModelConfigured}
											readOnly={readOnly}
											onActionUpdate={(make, updatedModel) => handleUpdate(sectionId, make, updatedModel)}
											onActionValidation={(make, isValid) => handleValidation(sectionId, make, isValid)}
										/>
									</Grid>
								</Grid>
							))}
							topActions={[
								<CustomizedSwitches
									key={"simultaneousMarketShare"}
									textLeft={true}
									textChecked={"Simultaneous Market Shares"}
									textUnChecked={"Simultaneous Market Shares"}
									checked={Boolean(model?.tabsProps?.simultaneousMarketShare)}
									disabled={readOnly || Boolean(makes?.length <= 1)}
									onChange={(value) => handleUpdateSwitches(sectionId, "simultaneousMarketShare", value)}
									tooltip={{
										title: "Performing in multiple market shares simultaneously. Makes all tabs share the same number of rows and columns.",
										placement: "bottom",
										key: "market-share-simultaneous-ranking-tooltip",
									}}
								/>,
							]}
							onTabChange={(_, information) => {
								tabInformation.current = information;
							}}
						/>
					)
				)}
			</Grid>
		</Grid>
	);
}
MarketShareMakes.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.shape({
		makes: PropTypes.array,
		simultaneousMarketShare: PropTypes.bool,
	}),
	excludesMakes: PropTypes.array,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};
